import React, { useEffect, useRef } from 'react'
import { useToast } from '@entur/alert'
import SearchResult from '../components/search/search-result'
import SearchForm from '../components/search/search-form'
import { FetchState } from '../api-clients/api-types'
import { useDownloadableApi, usePageableApi } from '../api-clients/client-hooks'
import { getTicketRightHolders, getTicketRightHoldersFile } from '../api-clients/eba-clients'
import { useStateWithSessionStorage } from '../hooks/common-hooks'
import { SearchHistory } from '../types'
import { trackEvent } from '../utils/tracking-utils'

const SearchPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useStateWithSessionStorage('currentPage', 1)
  const [savedResultsPerPage, saveResultsPerPage] = useStateWithSessionStorage('resultsPerPage', 10)
  const [savedQuery, saveQuery] = useStateWithSessionStorage<string>('search-query-state', '')
  const [response, doFetch] = usePageableApi(getTicketRightHolders)
  const [downloadResponse, download] = useDownloadableApi(getTicketRightHoldersFile)
  const init = useRef(true)
  const { addToast } = useToast()

  const downloadFile = async (format: string) => {
    addToast({ variant: 'info', content: 'Laster ned fil...' })
    download({
      query: `${savedQuery}&format=${format}`,
      backupFileName: `rettighetshavere.${format === 'addresses-pdf' ? 'pdf' : format}`,
    })
    trackEvent('Download-Search', { category: 'Download', format })
  }

  const fetchSearch = async (query: string, pageNumber: number, resultsPerPage: number) => {
    if (query !== '') {
      doFetch({
        query: `${query}&page=${pageNumber}&perPage=${resultsPerPage}&includeDeleted=true`,
      })
    }
  }

  const performSearch = async (query: string) => {
    saveQuery(query)
    setCurrentPage(1)
    await fetchSearch(query, 1, savedResultsPerPage)
  }

  const updatePage = async (pageNumber: number) => {
    setCurrentPage(pageNumber)
    await fetchSearch(savedQuery, pageNumber, savedResultsPerPage)
  }

  const updatePerPage = async (resultsPerPage: number) => {
    saveResultsPerPage(resultsPerPage)
    trackEvent('Update-Results-Per-Page', { category: 'Search', resultsPerPage })
    await fetchSearch(savedQuery, currentPage, resultsPerPage)
  }

  useEffect(() => {
    if (savedQuery && savedQuery !== '' && init.current) {
      doFetch({
        query: `${savedQuery}&page=${currentPage}&perPage=${savedResultsPerPage}&includeDeleted=true`,
      })
    }
    init.current = false
  }, [savedQuery, currentPage, savedResultsPerPage, doFetch, init])

  return (
    <div>
      <SearchForm performSearch={performSearch} isLoading={response.status === FetchState.LOADING} />
      <SearchResult
        searchState={
          {
            query: savedQuery,
            page: currentPage,
            perPage: savedResultsPerPage,
          } as SearchHistory
        }
        onPageChange={updatePage}
        onResultsPerPageChange={updatePerPage}
        downloadFile={downloadFile}
        response={response}
        downloadResponse={downloadResponse}
      />
    </div>
  )
}

export default SearchPage
